import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { BinIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useUnsubscribeAdditionalServiceMutation } from "@services/additional-services/mutations";
import type { IOrderedAdditionalServiceData } from "@services/additional-services/types";
import { useOverviewQuery } from "@services/overview/queries";
import { isNullOrUndefined } from "@utils/common";

interface IAdditionalServiceOrderConfirmModalProps extends IUseModalProps {
  jointBillServiceId: number;
  serviceId: number;
  jointBillServiceAgreementId?: IOrderedAdditionalServiceData["jointBillServiceAgreementId"];
}

export const AdditionalServiceUnsubscribeConfirmModal: React.FC<
  IAdditionalServiceOrderConfirmModalProps
> = ({
  jointBillServiceId,
  serviceId,
  jointBillServiceAgreementId,
  ...rest
}) => {
  const { t } = useTranslation();
  const modal = useModal(rest);
  const { data: overview } = useOverviewQuery();

  const { jointBillServices } = overview ?? { jointBillServices: [] };
  const jointBillService = jointBillServices.find(
    (jbs) => jbs.jointBillServiceId == jointBillServiceId,
  );
  const { toastApiError } = useApiErrorUtils();

  const { mutateAsync: unsubscribeService, isPending } =
    useUnsubscribeAdditionalServiceMutation(serviceId);

  const unsubscribe = async () => {
    const response = await unsubscribeService({
      jointBillServiceId,
      jointBillServiceAgreementId,
    }).catch(toastApiError);

    if (!isNullOrUndefined(response)) {
      toast.success(
        t("additionalService.unsubscribeConfirmModal.toast.success"),
      );

      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("additionalService.unsubscribeConfirmModal.title")}
      subtitle={jointBillService?.displayName ?? t("common.defaultAddress")}
      badgeProps={{ icon: BinIcon, color: "red" }}
      actionButtons={
        <>
          <Button onClick={modal.close} variant="secondary" className="w-full">
            {t("additionalService.unsubscribeConfirmModal.buttons.cancel")}
          </Button>
          <Button
            onClick={unsubscribe}
            className="w-full"
            isLoading={isPending}
          >
            {t("additionalService.unsubscribeConfirmModal.buttons.accept")}
          </Button>
        </>
      }
    />
  );
};
